import { format as formatNumber } from 'd3-format'
import type {
  FinancialStatementDataSchema,
  FinancialStatementPeriod,
  FinancialStatementRow,
  FinancialStatementRowKind,
  FinancialStatementStatementUI,
  FinancialStatementUISchema,
} from './types'

export const FINANCIAL_STATEMENT_EMPTY_AMOUNT = '—'

const VALUE_ROW_KINDS: ReadonlySet<FinancialStatementRowKind> = new Set(['line', 'subtotal', 'total'])
const INDENT_ROW_KINDS: ReadonlySet<FinancialStatementRowKind> = new Set(['line', 'subtotal'])

export interface FinancialStatementViewRow {
  id: string
  kind: FinancialStatementRowKind
  label?: string
  level: number
  values: Record<string, string>
  tooltip?: string
}

export interface FinancialStatementView {
  id: string
  title: string
  subtitle?: string
  asOf?: string
  kind?: FinancialStatementStatementUI['kind']
  periods: FinancialStatementPeriod[]
  rows: FinancialStatementViewRow[]
}

export function formatFinancialStatementAmount(value: number | null | undefined, amountFormat?: string): string {
  if (value === null || value === undefined) {
    return FINANCIAL_STATEMENT_EMPTY_AMOUNT
  }

  if (amountFormat) {
    try {
      return formatNumber(amountFormat)(value)
    } catch {
      // fall through to locale formatting
    }
  }

  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value)
}

function resolveRowLevel(row: FinancialStatementRow): number {
  if (!INDENT_ROW_KINDS.has(row.kind)) {
    return 0
  }
  return row.level ?? 0
}

function resolveRowValues(
  row: FinancialStatementRow,
  periods: FinancialStatementPeriod[],
  amountFormat?: string
): Record<string, string> {
  if (!VALUE_ROW_KINDS.has(row.kind)) {
    return {}
  }

  return Object.fromEntries(
    periods.map((period) => [period.id, formatFinancialStatementAmount(row.values?.[period.id], amountFormat)])
  )
}

function resolveRow(row: FinancialStatementRow, periods: FinancialStatementPeriod[], amountFormat?: string): FinancialStatementViewRow {
  return {
    id: row.id,
    kind: row.kind,
    label: row.kind === 'spacer' ? undefined : row.label,
    level: resolveRowLevel(row),
    values: resolveRowValues(row, periods, amountFormat),
    tooltip: row.tooltip,
  }
}

export function resolveFinancialStatementView(
  uiSchema: FinancialStatementUISchema,
  dataSchema: FinancialStatementDataSchema
): FinancialStatementView[] {
  const dataById = new Map(dataSchema.statements.map((statement) => [statement.id, statement]))

  return uiSchema.statements.flatMap((statementUi) => {
    const statementData = dataById.get(statementUi.id)
    if (!statementData) {
      return []
    }

    return [
      {
        id: statementUi.id,
        title: statementUi.title,
        subtitle: statementUi.subtitle,
        asOf: statementUi.asOf,
        kind: statementUi.kind,
        periods: statementUi.periods,
        rows: statementData.rows.map((row) => resolveRow(row, statementUi.periods, uiSchema.amountFormat)),
      },
    ]
  })
}
